import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { BarChart3, TrendingUp, TrendingDown, Eye, Crown, ArrowUpRight, Minus } from 'lucide-react'
import { getCreatorOverview, type CreatorOverview } from '../lib/api'

export default function CreatorDashboard() {
  const [overview, setOverview] = useState<CreatorOverview | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    getCreatorOverview()
      .then(setOverview)
      .catch((err) => setError(err.message || 'Failed to load analytics'))
      .finally(() => setLoading(false))
  }, [])

  if (loading) {
    return (
      <div>
        <div className="mb-8">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="w-6 h-6" />
            My Analytics
          </h1>
          <p className="text-muted-foreground mt-1">See how your apps are performing</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-36 animate-pulse bg-secondary rounded-xl" />
          ))}
        </div>
        <div className="h-64 animate-pulse bg-secondary rounded-xl mt-6" />
      </div>
    )
  }

  if (error) {
    return (
      <div>
        <div className="mb-8">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="w-6 h-6" />
            My Analytics
          </h1>
          <p className="text-muted-foreground mt-1">See how your apps are performing</p>
        </div>
        <div className="p-6 rounded-xl border border-destructive/30 bg-destructive/5 text-center">
          <p className="text-destructive mb-4">{error}</p>
          <button
            onClick={() => {
              setLoading(true)
              setError(null)
              getCreatorOverview()
                .then(setOverview)
                .catch((err) => setError(err.message || 'Failed to load analytics'))
                .finally(() => setLoading(false))
            }}
            className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  if (!overview || overview.total_apps === 0) {
    return (
      <div>
        <div className="mb-8">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="w-6 h-6" />
            My Analytics
          </h1>
          <p className="text-muted-foreground mt-1">See how your apps are performing</p>
        </div>
        <div className="p-12 rounded-xl border border-border bg-card text-center">
          <Eye className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-lg font-semibold mb-2">No apps yet</h2>
          <p className="text-muted-foreground mb-6">Create your first app to see analytics here</p>
          <Link
            to="/apps"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            <ArrowUpRight className="w-4 h-4" />
            Go to My Apps
          </Link>
        </div>
      </div>
    )
  }

  const wowSign = overview.wow_change > 0 ? '+' : ''
  const wowColor = overview.wow_change > 0
    ? 'text-green-500'
    : overview.wow_change < 0
      ? 'text-red-500'
      : 'text-muted-foreground'
  const WowIcon = overview.wow_change > 0
    ? TrendingUp
    : overview.wow_change < 0
      ? TrendingDown
      : Minus

  const sortedApps = [...overview.app_stats].sort((a, b) => b.visit_count_30d - a.visit_count_30d)

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <BarChart3 className="w-6 h-6" />
          My Analytics
        </h1>
        <p className="text-muted-foreground mt-1">See how your apps are performing</p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {/* Total Visitors */}
        <div className="rounded-xl border border-border bg-card p-6">
          <div className="flex items-center gap-2 text-muted-foreground mb-3">
            <Eye className="w-5 h-5" />
            <span className="text-sm font-medium">Total Visitors</span>
          </div>
          <p className="text-2xl font-bold">{overview.total_visitors_30d.toLocaleString()}</p>
          <p className="text-sm text-muted-foreground mt-1">
            visitors in 30 days
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {overview.total_unique_visitors_30d.toLocaleString()} unique
          </p>
        </div>

        {/* Most Visited App */}
        <div className="rounded-xl border border-border bg-card p-6">
          <div className="flex items-center gap-2 text-muted-foreground mb-3">
            <Crown className="w-5 h-5" />
            <span className="text-sm font-medium">Most Visited App</span>
          </div>
          {overview.most_visited_app ? (
            <>
              <Link
                to={`/apps/${overview.most_visited_app}`}
                className="text-xl font-bold text-primary hover:underline"
              >
                {overview.most_visited_app}
              </Link>
              <p className="text-sm text-muted-foreground mt-1">
                {overview.most_visited_count.toLocaleString()} visits
              </p>
            </>
          ) : (
            <p className="text-xl font-bold text-muted-foreground">None yet</p>
          )}
        </div>

        {/* Week over Week */}
        <div className="rounded-xl border border-border bg-card p-6">
          <div className="flex items-center gap-2 text-muted-foreground mb-3">
            <WowIcon className="w-5 h-5" />
            <span className="text-sm font-medium">Week over Week</span>
          </div>
          <p className={`text-2xl font-bold ${wowColor}`}>
            {wowSign}{overview.wow_change.toFixed(1)}%
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            {overview.this_week_visits} this week vs {overview.last_week_visits} last week
          </p>
        </div>
      </div>

      {/* Per-App Breakdown */}
      <div className="rounded-xl border border-border bg-card p-6">
        <h2 className="text-lg font-semibold mb-4">Per-App Breakdown</h2>
        {sortedApps.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left text-sm font-medium text-muted-foreground py-3 pr-4">App Name</th>
                  <th className="text-left text-sm font-medium text-muted-foreground py-3 pr-4">Display Name</th>
                  <th className="text-right text-sm font-medium text-muted-foreground py-3 pr-4">Visitors (30d)</th>
                  <th className="text-right text-sm font-medium text-muted-foreground py-3">Unique Visitors (30d)</th>
                </tr>
              </thead>
              <tbody>
                {sortedApps.map((app) => (
                  <tr key={app.app_name} className="border-b border-border last:border-0">
                    <td className="py-3 pr-4">
                      <Link
                        to={`/apps/${app.app_name}`}
                        className="text-sm font-medium text-primary hover:underline"
                      >
                        {app.app_name}
                      </Link>
                    </td>
                    <td className="py-3 pr-4 text-sm text-muted-foreground">
                      {app.display_name || '-'}
                    </td>
                    <td className="py-3 pr-4 text-right text-sm font-medium">
                      {app.visit_count_30d.toLocaleString()}
                    </td>
                    <td className="py-3 text-right text-sm font-medium">
                      {app.unique_visitors_30d.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-center text-muted-foreground py-8">No visitor data yet</p>
        )}
      </div>
    </div>
  )
}
